// ##### Travel Statistics Calculation Functions
// Functions to calculate various travel statistics for the stats cards

import { calculateDuration, calculateDaysUntil } from 'shared_items/formatConventions';
import { getCurrentYear, isDateInCurrentYear, validateTripData } from './travel_utils';

// ##### Calculate comprehensive travel statistics
export const calculateTravelStats = (trips) => {
  if (!trips || !Array.isArray(trips) || trips.length === 0) {
    return {
      totalCountries: 0,
      totalTrips: 0,
      totalDaysTraveling: 0,
      meanDuration: 0,
      yearTrips: 0,
      lastTrip: { date: null, title: 'No info yet' },
      nextTrip: { date: null, title: 'No info yet', daysUntil: 0 }
    };
  }

  const today = new Date();
  const currentYear = getCurrentYear();
  
  // Initialize counters
  let totalTrips = 0;
  let totalDaysTraveling = 0;
  let yearTrips = 0;
  let lastTrip = null;
  let nextTrip = null;
  let minFutureDate = null;
  let maxPastDate = null;
  
  // Track unique countries
  const uniqueCountries = new Set();
  
  trips.forEach(trip => {
    if (!validateTripData(trip)) {
      return;
    }
    
    // Count unique countries from places
    trip.places.forEach(place => {
      if (place.country) {
        uniqueCountries.add(place.country);
      }
    });
    
    // Process trip dates
    const startDate = new Date(trip.start_date);
    const endDate = new Date(trip.end_date);
    
    // Check if trip is in current year
    if (isDateInCurrentYear(trip.start_date)) {
      yearTrips++;
    }
    
    // Calculate duration and add to total
    const duration = calculateDuration(trip.start_date, trip.end_date);
    totalDaysTraveling += duration;
    totalTrips++;
    
    // Track last completed trip (end_date <= today)
    if (endDate <= today) {
      if (!maxPastDate || endDate > maxPastDate) {
        maxPastDate = endDate;
        lastTrip = {
          date: trip.end_date,
          title: trip.title
        };
      }
    }
    
    // Track next planned trip (start_date > today)
    if (startDate > today) {
      if (!minFutureDate || startDate < minFutureDate) {
        minFutureDate = startDate;
        nextTrip = {
          date: trip.start_date,
          title: trip.title,
          daysUntil: calculateDaysUntil(trip.start_date)
        };
      }
    }
  });
  
  // Calculate mean duration
  const meanDuration = totalTrips > 0 ? Math.round(totalDaysTraveling / totalTrips) : 0;
  
  return {
    totalCountries: uniqueCountries.size,
    totalTrips,
    totalDaysTraveling,
    meanDuration,
    yearTrips,
    lastTrip: lastTrip || { date: null, title: 'No info yet' },
    nextTrip: nextTrip || { date: null, title: 'No info yet', daysUntil: 0 }
  };
};

// ##### Get current year trips count
export const getCurrentYearTrips = (trips) => {
  if (!trips || !Array.isArray(trips)) return 0;
  
  let yearTrips = 0;
  
  trips.forEach(trip => {
    if (trip.start_date && isDateInCurrentYear(trip.start_date)) {
      yearTrips++;
    }
  });
  
  return yearTrips;
};

// ##### Get last and next trip information
export const getLastAndNextTrip = (trips) => {
  if (!trips || !Array.isArray(trips) || trips.length === 0) {
    return {
      lastTrip: { date: null, title: 'No info yet' },
      nextTrip: { date: null, title: 'No info yet', daysUntil: 0 }
    };
  }
  
  const today = new Date();
  let lastTrip = null;
  let nextTrip = null;
  let minFutureDate = null;
  let maxPastDate = null;
  
  trips.forEach(trip => {
    if (!validateTripData(trip)) {
      return;
    }
    
    const startDate = new Date(trip.start_date);
    const endDate = new Date(trip.end_date);
    
    // Track last completed trip (end_date <= today)
    if (endDate <= today) {
      if (!maxPastDate || endDate > maxPastDate) {
        maxPastDate = endDate;
        lastTrip = {
          date: trip.end_date,
          title: trip.title
        };
      }
    }
    
    // Track next planned trip (start_date > today)
    if (startDate > today) {
      if (!minFutureDate || startDate < minFutureDate) {
        minFutureDate = startDate;
        nextTrip = {
          date: trip.start_date,
          title: trip.title,
          daysUntil: calculateDaysUntil(trip.start_date)
        };
      }
    }
  });
  
  return {
    lastTrip: lastTrip || { date: null, title: 'No info yet' },
    nextTrip: nextTrip || { date: null, title: 'No info yet', daysUntil: 0 }
  };
};
